import React, { memo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { useRecoilValueLoadable } from 'recoil';
import Box from '@mui/material/Box';

import { selectRegions } from 'selectors/regions';
import { EMPTY_FUNCTION } from 'constants/common';

const getOptionLabel = option => `${option.id} - ${option.name}`;
const getOptionSelected = (option, value) => option.id === value.id;

/**
 * Select region step. Shows autocomplete input to choose region
 * @note fakes passed inputRef to look/feel as an input with value of selected id
 */
function RegionsSearch({
  defaultValue,
  inputProps,
  productId,
  analog,
  inputRef,
  textFieldProps,
  onSelect,
  readonly,
  error,
  ...props
}) {
  const [id, setId] = useState(() => {
    const value = defaultValue ? +defaultValue : null;
    // eslint-disable-next-line no-param-reassign
    inputRef.current = { value };
    return value;
  });
  const regionsLoadable = useRecoilValueLoadable(
    selectRegions({ productId, analog }),
  );
  const isLoading = regionsLoadable.state === 'loading';
  const { items, byId } = isLoading
    ? { items: [], byId: {} }
    : regionsLoadable.contents;

  const renderInput = useCallback(
    ({ inputProps: ownProps, ...restInputProps }) => {
      const { size, label } = textFieldProps;
      const combinedInputProps = { ...ownProps, ...inputProps };
      return (
        <TextField
          {...restInputProps}
          error={error}
          size={size}
          label={label}
          slotProps={{ htmlInput: combinedInputProps }}
          variant="outlined"
          placeholder="Select region"
        />
      );
    },
    [inputProps, textFieldProps, error],
  );

  const handleSelect = useCallback(
    (_, regionData) => {
      const newId = regionData?.id ?? null;
      setId(newId);
      // eslint-disable-next-line no-param-reassign
      inputRef.current = { value: newId };
      onSelect(newId);
    },
    [setId, inputRef, onSelect],
  );

  const value = byId[id] ?? null;

  return (
    <Box position="relative" width="100%">
      <Autocomplete
        {...props}
        disabled={isLoading || readonly}
        clearOnBlur
        options={items}
        value={value}
        onChange={handleSelect}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={getOptionSelected}
        renderInput={renderInput}
        disablePortal
      />
    </Box>
  );
}

RegionsSearch.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  inputProps: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  textFieldProps: PropTypes.object,
  productId: PropTypes.number,
  analog: PropTypes.bool,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.shape({}) }),
  ]).isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func,
  readonly: PropTypes.bool,
  error: PropTypes.bool,
};

RegionsSearch.defaultProps = {
  error: false,
  defaultValue: null,
  productId: null,
  analog: false,
  textFieldProps: {
    size: 'small',
  },
  onSelect: EMPTY_FUNCTION,
  readonly: false,
};

export default memo(RegionsSearch);
